import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  styled,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useView } from '../../utils/PersonalViewProvider';
import api from '../../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const HeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `2px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  alignItems: 'center',
}));

const DataRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1.5),
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AccuracyChip = styled(Chip)(({ accuracy }) => {
  let color = 'error';
  if (accuracy >= 80) color = 'success';
  else if (accuracy >= 50) color = 'warning';

  return {
    fontWeight: 'bold',
    minWidth: '60px',
    color,
  };
});

/**
 * Renders a grouped breakdown table (by person / project / track) of
 * estimated vs actual time.
 */
const BreakdownTable = ({ title, rows, includeProject }) => (
  <StyledCard>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <HeaderRow container spacing={2}>
        <Grid size={includeProject ? 3 : 4}>
          <Typography variant="caption">Name</Typography>
        </Grid>
        {includeProject && (
          <Grid size={2}>
            <Typography variant="caption">Project</Typography>
          </Grid>
        )}
        <Grid size={1}>
          <Typography variant="caption">Tasks</Typography>
        </Grid>
        <Grid size={includeProject ? 2 : 2}>
          <Typography variant="caption">Estimated (h)</Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="caption">Actual (h)</Typography>
        </Grid>
        <Grid size={1}>
          <Typography variant="caption">Variance (h)</Typography>
        </Grid>
        <Grid size={1}>
          <Typography variant="caption">Accuracy</Typography>
        </Grid>
      </HeaderRow>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No data available.
        </Typography>
      ) : (
        rows.map((row) => (
          <DataRow key={`${title}-${row.name}`} container spacing={2}>
            <Grid size={includeProject ? 3 : 4}>
              <Typography variant="subtitle1">{row.name}</Typography>
            </Grid>
            {includeProject && (
              <Grid size={2}>
                <Typography variant="body2" color="text.secondary">
                  {row.project}
                </Typography>
              </Grid>
            )}
            <Grid size={1}>
              <Typography variant="body2">{row.task_count}</Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2">{row.estimated_hours}</Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2">{row.actual_hours}</Typography>
            </Grid>
            <Grid size={1}>
              <Typography
                variant="body2"
                color={row.variance > 0 ? 'error.main' : row.variance < 0 ? 'success.main' : 'text.primary'}
              >
                {row.variance > 0 ? '+' : ''}{row.variance}
              </Typography>
            </Grid>
            <Grid size={1}>
              <AccuracyChip
                label={`${row.accuracy_percentage.toFixed(0)}%`}
                accuracy={row.accuracy_percentage}
                variant="outlined"
                size="small"
              />
            </Grid>
          </DataRow>
        ))
      )}
    </CardContent>
  </StyledCard>
);

/**
 * Flat list of the individual time logs behind the report, newest first, with
 * a shortcut to the task / action item each one was logged against.
 */
const TimeLogsTable = ({ logs, showWorkspace, onOpen }) => (
  <StyledCard>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Time Logs
      </Typography>
      {logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No time logs in this range.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Date</TableCell>
              <TableCell>Person</TableCell>
              {showWorkspace && <TableCell>Workspace</TableCell>}
              <TableCell>Project</TableCell>
              <TableCell>Task / Meeting</TableCell>
              <TableCell>Action Item</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell padding="checkbox">
                  <Tooltip title={log.task_id ? 'Open task' : 'Meeting log'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!log.task_id}
                        onClick={() => onOpen(log)}
                        aria-label="Open task"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{dayjs(log.start_time).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>{log.attendee || ''}</TableCell>
                {showWorkspace && <TableCell>{log.workspace_name || ''}</TableCell>}
                <TableCell>{log.project || ''}</TableCell>
                <TableCell>{log.task || (log.meeting ? `Meeting: ${log.meeting}` : '')}</TableCell>
                <TableCell>{log.action_item || ''}</TableCell>
                <TableCell align="right">{log.duration.toFixed(2)}</TableCell>
                <TableCell>{log.notes || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </StyledCard>
);

const TimeLogsReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allWorkspaces, setAllWorkspaces] = useState(false);
  const { currentWorkspace, workspaces, selectWorkspace } = useWorkspace();
  const { selectedAssignee } = useView();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const fetchReport = async () => {
      if (!currentWorkspace?.id) {
        setReportData(null);
        setLoading(false);
        return;
      }
      try {
        const params = {
          ...(allWorkspaces ? { all_workspaces: true } : { workspace_id: currentWorkspace.id }),
          ...(selectedAssignee ? { assignee: selectedAssignee } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        };
        const response = await api.get('time-logs-report/', { params });
        setReportData(response.data.payload);
      } catch (error) {
        setReportData(null);
      }
      setLoading(false);
    };
    fetchReport();
  }, [currentWorkspace, selectedAssignee, startDate, endDate, allWorkspaces]);

  // Task details load from the current workspace, so switch first when the
  // log belongs to another one.
  const handleOpenLog = (log) => {
    if (!log.task_id) return;
    if (log.workspace && log.workspace !== currentWorkspace?.id) {
      const target = workspaces.find((ws) => ws.id === log.workspace);
      if (target) selectWorkspace(target);
    }
    const query = log.action_item_id ? `?action_item=${log.action_item_id}` : '';
    navigate(`/tasks/${log.task_id}${query}`);
  };

  const setPreset = (days) => {
    setEndDate(dayjs().format('YYYY-MM-DD'));
    setStartDate(dayjs().subtract(days - 1, 'day').format('YYYY-MM-DD'));
  };

  const filters = (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
      <TextField
        type="date"
        label="From"
        size="small"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        type="date"
        label="To"
        size="small"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Button size="small" onClick={() => setPreset(7)}>Last 7 days</Button>
      <Button size="small" onClick={() => setPreset(30)}>Last 30 days</Button>
      <Button
        size="small"
        onClick={() => { setStartDate(''); setEndDate(''); }}
        disabled={!startDate && !endDate}
      >
        All time
      </Button>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={allWorkspaces}
            onChange={(e) => setAllWorkspaces(e.target.checked)}
          />
        }
        label="All workspaces"
      />
    </Box>
  );

  if (loading) return <Box sx={{ padding: 2 }}>{filters}<div>Loading...</div></Box>;

  if (!reportData) {
    return <Box sx={{ padding: 2 }}>{filters}<Alert severity="info">No data available</Alert></Box>;
  }

  const {
    overall,
    by_person: byPerson,
    by_project: byProject,
    by_track: byTrack,
    time_logs: timeLogs = [],
  } = reportData;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Time spent vs estimated, by person / project / track
        {allWorkspaces && reportData.workspace ? ` — ${reportData.workspace}` : ''}
      </Typography>

      {filters}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="secondary">
                {overall.estimated_hours}h
              </Typography>
              <Typography variant="body2">Estimated Hours</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {overall.actual_hours}h
              </Typography>
              <Typography variant="body2">Actual Hours</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography
                variant="h6"
                color={overall.variance > 0 ? 'error.main' : 'success.main'}
              >
                {overall.variance > 0 ? '+' : ''}{overall.variance}h
              </Typography>
              <Typography variant="body2">Overall Variance</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="primary">
                {overall.accuracy_percentage.toFixed(1)}%
              </Typography>
              <Typography variant="body2">Overall Accuracy</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        {overall.meeting_hours > 0 && (
          <Grid size={3}>
            <MetricCard>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {overall.meeting_hours}h
                </Typography>
                <Typography variant="body2">Meeting Hours (not in task totals)</Typography>
              </CardContent>
            </MetricCard>
          </Grid>
        )}
      </Grid>

      <BreakdownTable title="By Person" rows={byPerson} />
      <BreakdownTable title="By Project" rows={byProject} />
      <BreakdownTable title="By Track" rows={byTrack} includeProject />
      <TimeLogsTable logs={timeLogs} showWorkspace={allWorkspaces} onOpen={handleOpenLog} />
    </Box>
  );
};

export default TimeLogsReport;
